import { PanelTitle } from "@/components/shared/panel-title";
import { type ChipTone, StatusChip } from "@/components/shared/status-chip";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { money } from "@/lib/format";

export interface Diagnostics {
  /** Comes from the API so Finance can change it without a deploy -- a literal
   *  here would bury a business rule where only a deploy could change it. */
  internal_share_threshold?: number;
  overdue: { count: number; value: number; oldest_due: string | null };
  /** Aggregate users get the percentages only: the engine omits the hours and
   *  the two rates (a rate is revenue or cost per hour, so it carries hours). */
  internal: { internal_hours?: number; billable_hours?: number; internal_share_pct: number | null };
  rates: { effective_rate?: number | null; card_rate?: number | null; gap_pct: number | null };
  budget: { over_budget_projects: number; projects_without_budget: number };
  /** Why the account is short of the benchmark, in dollars. Absent when the
   *  account has no revenue in range, or from an older server. */
  gap?: {
    revenue: number;
    actual_profit: number;
    target_profit: number | null;
    shortfall: number | null;
    unattributed_cost: number;
    unattributed_pct_of_revenue: number;
    projects_below_benchmark: {
      entity_id: string;
      project_name: string;
      revenue: number;
      margin_pct: number | null;
      /** Absent for Aggregate: revenue x benchmark less profit gives cost back. */
      shortfall?: number;
      /** A resale cannot be levered; named, but never counted as actionable. */
      is_passthrough?: boolean;
    }[];
  };
}

// Only used when the API omits the field entirely (an older server, a stub in
// a test). Real traffic always carries diagnostics.internal_share_threshold.
const FALLBACK_INTERNAL_SHARE_THRESHOLD = 15;

/**
 * The four questions Sales Role Team leads ask about a bad-margin account:
 * are invoices going unpaid, is too much time going to internal/AM work, are
 * rates stale or below card, is scope creeping past budget.
 *
 * A finding appears only when the data supports it. Missing data -- no rate
 * card for these roles, no budget set on a project -- is reported as missing,
 * a caveat, never folded into a finding as if it were a measured zero.
 */
export function DiagnosticsPanel({ diagnostics }: { diagnostics: Diagnostics }) {
  const findings: { label: string; headline: string; tone: ChipTone; detail: string }[] = [];

  // The fork first, because it decides which lever applies. The CFO's rule:
  // if a delivery project is under benchmark it is a rate/resourcing problem;
  // if delivery is healthy and the account still misses, it is non-billable
  // Account Management investment. Everything below is a supporting detail.
  const benchmarkGap = diagnostics.gap;
  if (benchmarkGap?.shortfall != null && benchmarkGap.shortfall > 0) {
    const worst = benchmarkGap.projects_below_benchmark;
    // Resales are excluded from the actionable total: reselling a licence at
    // cost cannot be levered, so counting it as "delivery drag" would send
    // someone to negotiate a rate that does not exist.
    const deliveryDrag = worst
      .filter((p) => !p.is_passthrough)
      .reduce((sum, p) => sum + (p.shortfall ?? 0), 0);
    // Per-project dollars are withheld from Aggregate users; then the projects
    // are named with their margin instead, and no delivery total is claimed.
    const perProjectDollars = worst.every((p) => p.shortfall !== undefined);
    if (worst.length > 0 && !perProjectDollars) {
      findings.push({
        label: "Delivery is dragging this account",
        tone: "bad",
        headline: `${money(benchmarkGap.shortfall)} below benchmark`,
        detail:
          `${money(benchmarkGap.shortfall)} below the benchmark overall. Projects below it: ` +
          worst
            .slice(0, 3)
            .map(
              (p) =>
                `${p.project_name} (${p.margin_pct === null ? "no margin" : `${p.margin_pct.toFixed(1)}% margin`}${p.is_passthrough ? ", a resale -- no lever" : ""})`,
            )
            .join(", ") +
          `. Levers sit on the project: bill rate, resource mix, spread of time.`,
      });
    } else if (worst.length > 0) {
      findings.push({
        label: "Delivery is dragging this account",
        tone: "bad",
        headline: `${money(deliveryDrag)} of the gap`,
        detail:
          `${money(benchmarkGap.shortfall)} below the benchmark overall. ` +
          `${money(deliveryDrag)} of it is delivery: ` +
          worst
            .slice(0, 3)
            .map(
              (p) =>
                `${p.project_name} (${money(p.shortfall ?? 0)} short${p.is_passthrough ? ", a resale -- no lever" : ""})`,
            )
            .join(", ") +
          `. Levers sit on the project: bill rate, resource mix, spread of time.`,
      });
    }
    if (benchmarkGap.unattributed_cost > 0) {
      findings.push({
        label: "Internal investment in this account",
        tone: "warn",
        headline: `${money(benchmarkGap.unattributed_cost)} of cost`,
        detail:
          `${money(benchmarkGap.unattributed_cost)} of cost sits on the account and on no delivery ` +
          `project -- Account Management and other non-billable account time. ` +
          `That is ${benchmarkGap.unattributed_pct_of_revenue.toFixed(1)}% of revenue, and the same ` +
          `in margin points. Confirm it is a deliberate growth bet; if not, trim coverage.`,
      });
    }
  }

  if (diagnostics.overdue.count > 0) {
    const oldest = diagnostics.overdue.oldest_due
      ? `, oldest due ${diagnostics.overdue.oldest_due}`
      : "";
    findings.push({
      label: "Invoices not being paid",
      tone: "warn",
      headline: `${diagnostics.overdue.count} overdue`,
      detail: `${diagnostics.overdue.count} overdue, ${money(diagnostics.overdue.value)} outstanding${oldest}`,
    });
  }

  const share = diagnostics.internal.internal_share_pct;
  const threshold = diagnostics.internal_share_threshold ?? FALLBACK_INTERNAL_SHARE_THRESHOLD;
  if (share !== null && share > threshold) {
    findings.push({
      label: "Internal hours on this account",
      tone: "warn",
      headline: `${share.toFixed(1)}% non-billable`,
      detail:
        diagnostics.internal.internal_hours === undefined
          ? `${share.toFixed(1)}% of hours are non-billable, above the ${threshold}% threshold`
          : `${share.toFixed(1)}% of hours are non-billable (${diagnostics.internal.internal_hours.toFixed(0)} hrs), above the ${threshold}% threshold`,
    });
  }

  // Absent (Aggregate) reads as null: the rate finding and its caveat both
  // need the dollar rates, so neither appears without them.
  const rate = diagnostics.rates.effective_rate ?? null;
  const card = diagnostics.rates.card_rate ?? null;
  const gap = diagnostics.rates.gap_pct;
  if (rate !== null && card !== null && gap !== null && gap < 0) {
    findings.push({
      label: "Rate below the rate card",
      tone: "warn",
      headline: `${gap.toFixed(1)}% below card`,
      detail: `Billing $${rate.toFixed(0)}/hr against a card rate of $${card.toFixed(0)}/hr (${gap.toFixed(1)}%)`,
    });
  }

  if (diagnostics.budget.over_budget_projects > 0) {
    findings.push({
      label: "Projects over budget",
      tone: "bad",
      headline: `${diagnostics.budget.over_budget_projects} project(s)`,
      detail: `${diagnostics.budget.over_budget_projects} project(s) past their budgeted hours`,
    });
  }

  const caveats: string[] = [];
  if (rate !== null && card === null) {
    caveats.push(
      "No rate card exists for the roles on this account, so rate staleness cannot be assessed.",
    );
  }
  if (diagnostics.budget.projects_without_budget > 0) {
    caveats.push(
      `${diagnostics.budget.projects_without_budget} project(s) have no budget set, so hours burn cannot be assessed for them.`,
    );
  }

  const shortfall = benchmarkGap?.shortfall ?? 0;

  return (
    <Card className="w-full">
      <CardHeader className="flex items-baseline justify-between">
        <PanelTitle help="The four questions asked of a bad-margin account: are invoices unpaid, is too much time internal, are rates below card, is scope past budget. A finding appears only when the data supports it.">
          Why this margin
        </PanelTitle>
        {shortfall > 0 ? (
          <span className="text-xs text-muted-foreground">{money(shortfall)} below benchmark</span>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {findings.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No issues found in invoices, internal hours, rates or budgets.
          </p>
        ) : (
          <ul className="space-y-2">
            {findings.map((f) => (
              <li key={f.label} className="space-y-1">
                <div className="flex flex-wrap items-center gap-2 font-display text-[13px] font-semibold">
                  <StatusChip tone={f.tone}>{f.label}</StatusChip>
                  <span className="tabular-nums">{f.headline}</span>
                </div>
                <p className="text-[13.5px] leading-relaxed text-muted-foreground">{f.detail}</p>
              </li>
            ))}
          </ul>
        )}
        {caveats.length > 0 && (
          <div className="space-y-1 border-t pt-3 text-xs text-muted-foreground">
            {caveats.map((c) => (
              <p key={c}>{c}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
